import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import SendIcon from '@mui/icons-material/Send';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useGetMembersQuery } from '@modules/book-ticket';

import type { ChatMemberMentionCandidate } from '../../utils/chatMemberMention.utils';
import type { ChatMessageItem } from '../../types/chat.types';
import type { ChatResponse, ChatStateOptions } from '../../api/chat.api';

import { parseFetchChatError, usePostChatMutation } from '../../api/chat.api';
import ChatMemberMentionList from '../ChatMemberMentionList';
import { useChatMemberMention } from '../../hooks/useChatMemberMention';
import {
  buildAvailabilityPickMessage,
  buildAvailabilityWindowPickMessage,
  buildCoursesPickMessage,
  buildTeeTimePickMessage,
  formatAvailabilityLabel,
  formatDraftSummary,
  formatStateDetail,
  isMessageSpacerLine,
  markdownToTextLines,
  renderInlineMarkdown,
  stripIds,
  ticketSuccessLine,
} from '../../utils/chatMessage.utils';

interface PendingCourseSelection {
  id: string;
  label: string;
}

function hasPickOptions(options?: ChatStateOptions | null): boolean {
  if (!options) {
    return false;
  }

  return Boolean(
    options.courses?.length ||
      options.teeTimePresets?.length ||
      options.availabilityPresets?.length ||
      options.availabilityWindowPresets?.length,
  );
}

const ChatAssistant: React.FC = () => {
  const theme = useTheme();
  const [messages, setMessages] = useState<ChatMessageItem[]>([]);
  const [conversationId, setConversationId] = useState<string | undefined>();
  const [latestState, setLatestState] = useState<ChatResponse['state']>(null);
  const [input, setInput] = useState('');
  const [cursorIndex, setCursorIndex] = useState(0);
  const [pendingCourses, setPendingCourses] = useState<PendingCourseSelection[]>([]);
  const sendInFlightRef = useRef(false);
  const scrollAnchorRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  const [postChat, { isLoading: loading }] = usePostChatMutation();
  const { data: membersResponse } = useGetMembersQuery();

  const roster = useMemo<ChatMemberMentionCandidate[]>(
    () =>
      (membersResponse?.data ?? []).map((member) => ({
        id: member.id,
        fullName: member.fullName,
      })),
    [membersResponse?.data],
  );

  const { suggestions, showSuggestions, applyMention } = useChatMemberMention(
    roster,
    input,
    cursorIndex,
  );

  const canSend = input.trim().length > 0 && !loading;

  useEffect(() => {
    setMessages([]);
    setConversationId(undefined);
    setLatestState(null);
    setInput('');
    setPendingCourses([]);
  }, []);

  useEffect(() => {
    if ((latestState?.courses?.length ?? 0) >= 2) {
      setPendingCourses([]);
    }
  }, [latestState?.courses]);

  useEffect(() => {
    scrollAnchorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, loading, latestState]);

  const appendAssistantFromResponse = useCallback((data: ChatResponse) => {
    if (data.conversationId) {
      setConversationId(data.conversationId);
    }

    setLatestState(data.state ?? null);

    const replyContent = data.reply ?? '';
    const stateDetail = formatStateDetail(data);
    const ticketLine = data.ticket ? ticketSuccessLine(data.ticket) : undefined;
    const errorLine = data.error?.trim() ? `Note: ${data.error.trim()}` : undefined;
    const detail = [ticketLine, errorLine, stateDetail].filter(Boolean).join('\n\n') || undefined;

    const replyMessage: ChatMessageItem = {
      id: `assistant-${Date.now()}`,
      role: 'assistant',
      content: replyContent,
      detail,
    };

    setMessages((prev) => [...prev, replyMessage]);
  }, []);

  const handleSend = useCallback(
    async (messageOverride?: string) => {
      const text = (messageOverride ?? input).trim();

      if (!text || loading || sendInFlightRef.current) {
        return;
      }

      sendInFlightRef.current = true;
      setInput('');

      const userMessage: ChatMessageItem = {
        id: `user-${Date.now()}`,
        role: 'user',
        content: text,
      };

      setMessages((prev) => [...prev, userMessage]);

      try {
        const data: ChatResponse = await postChat({
          message: text,
          ...(conversationId && { conversationId }),
        }).unwrap();

        appendAssistantFromResponse(data);
      } catch (err) {
        const errText = parseFetchChatError(err);

        const assistantMessage: ChatMessageItem = {
          id: `assistant-${Date.now()}`,
          role: 'assistant',
          content:
            errText.includes('OPENAI') || errText.includes('Chat is not configured')
              ? 'Chat is not available on the server (check API logs / OPENAI_API_KEY).'
              : errText.trim()
                ? errText
                : 'Sorry, something went wrong. Please try again.',
        };

        setMessages((prev) => [...prev, assistantMessage]);
      } finally {
        sendInFlightRef.current = false;
      }
    },
    [appendAssistantFromResponse, conversationId, input, loading, postChat],
  );

  const handlePick = useCallback(
    (message: string) => {
      handleSend(message).catch(() => undefined);
    },
    [handleSend],
  );

  const handleSelectMention = useCallback(
    (member: ChatMemberMentionCandidate) => {
      const { nextInput, nextCursorIndex } = applyMention(member);

      setInput(nextInput);
      setCursorIndex(nextCursorIndex);
    },
    [applyMention],
  );

  const draftLines = latestState
    ? formatDraftSummary({ reply: '', conversationId: conversationId ?? '', state: latestState })
    : [];

  const options = latestState?.options;
  const showOptions = hasPickOptions(options) && !loading;
  const showCoursePicker = Boolean(options?.courses?.length);
  const pendingCourseIds = new Set(pendingCourses.map((course) => course.id));
  const canConfirmCourses = pendingCourses.length >= 2 && pendingCourses.length <= 4;

  const hasMoreOptionGroups = Boolean(
    options?.teeTimePresets?.length ||
      options?.availabilityPresets?.length ||
      options?.availabilityWindowPresets?.length,
  );

  const togglePendingCourse = useCallback((course: PendingCourseSelection) => {
    setPendingCourses((prev) => {
      if (prev.some((item) => item.id === course.id)) {
        return prev.filter((item) => item.id !== course.id);
      }

      if (prev.length >= 4) {
        return prev;
      }

      return [...prev, course];
    });
  }, []);

  const handleConfirmCourses = useCallback(() => {
    if (!canConfirmCourses) {
      return;
    }

    const message = buildCoursesPickMessage(
      pendingCourses.map((course, index) => ({
        label: course.label,
        priority: index + 1,
      })),
    );

    setPendingCourses([]);
    handlePick(message);
  }, [canConfirmCourses, handlePick, pendingCourses]);

  const offWhite = theme.palette.grey[100];
  const assistantBg = theme.palette.grey[200];
  const userFg = theme.palette.success.contrastText;

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: offWhite,
      }}
    >
      {draftLines.length > 0 && (
        <Box
          sx={{
            px: 2,
            py: 1.25,
            borderBottom: 1,
            borderColor: 'grey.300',
            bgcolor: 'background.paper',
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5 }}>
            Your request so far
          </Typography>
          {draftLines.map((line) => (
            <Typography key={line} variant="body2" sx={{ fontSize: 12, lineHeight: 1.4 }}>
              {line}
            </Typography>
          ))}
        </Box>
      )}

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          px: 2,
          py: 1.5,
        }}
      >
        {messages.length === 0 ? (
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{ minHeight: '100%', px: 3, py: 4, maxWidth: 420, mx: 'auto' }}
          >
            <Typography
              variant="subtitle1"
              fontWeight={600}
              textAlign="center"
              sx={{ mb: 2 }}
            >
              Schedule a tee time
            </Typography>
            <Stack spacing={1} sx={{ width: '100%' }}>
              <Typography color="text.secondary" sx={{ lineHeight: 1.6 }}>
                • Tell the assistant when you want to play
              </Typography>
              <Typography color="text.secondary" sx={{ lineHeight: 1.6 }}>
                • Add partners — use @ to pick a club member
              </Typography>
              <Typography color="text.secondary" sx={{ lineHeight: 1.6 }}>
                • Choose up to 4 courses in priority order
              </Typography>
              <Typography color="text.secondary" sx={{ lineHeight: 1.6 }}>
                • Set your availability window (1–3 hours)
              </Typography>
            </Stack>
            <Typography
              variant="caption"
              color="text.secondary"
              textAlign="center"
              sx={{ mt: 2, lineHeight: 1.5 }}
            >
              Guests can be added by name. Members must be selected with @.
            </Typography>
          </Stack>
        ) : (
          <Stack spacing={0.5} sx={{ pb: 1 }}>
            {messages.map((msg) => (
              <Box
                key={msg.id}
                sx={{
                  alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
                  maxWidth: '85%',
                  px: 2,
                  py: 1.5,
                  borderRadius: 2,
                  bgcolor: msg.role === 'user' ? 'success.main' : assistantBg,
                }}
              >
                {markdownToTextLines(msg.content).map((line, idx) => {
                  if (isMessageSpacerLine(line)) {
                    return (
                      <Box
                        key={`${msg.id}-spacer-${idx}`} // eslint-disable-line react/no-array-index-key
                        sx={{ height: 10 }}
                      />
                    );
                  }

                  const isBullet = /^(\d+\.\s+|[-*•]\s+)/.test(line.trim());
                  const normalized = line.replace(/^(\d+\.\s+|[-*•]\s+)/, '');

                  const fg = msg.role === 'user' ? userFg : theme.palette.text.primary;

                  return (
                    <Typography
                      key={`${msg.id}-line-${idx}`} // eslint-disable-line react/no-array-index-key
                      variant="body2"
                      sx={{
                        color: fg,
                        fontSize: 13,
                        lineHeight: 1.6,
                        mb: isBullet ? 0.75 : 0.5,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      {isBullet ? '• ' : ''}
                      {renderInlineMarkdown(stripIds(normalized), fg)}
                    </Typography>
                  );
                })}
                {msg.detail ? (
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      mt: 1,
                      color: 'text.secondary',
                      lineHeight: 1.5,
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {stripIds(msg.detail)}
                  </Typography>
                ) : null}
              </Box>
            ))}
            {loading && (
              <Box
                sx={{
                  alignSelf: 'flex-start',
                  maxWidth: '85%',
                  px: 2,
                  py: 1.5,
                  borderRadius: 2,
                  bgcolor: assistantBg,
                }}
              >
                <CircularProgress size={22} color="success" />
              </Box>
            )}
            <div ref={scrollAnchorRef} />
          </Stack>
        )}
      </Box>

      {showOptions && (
        <Box sx={{ px: 2, py: 1, borderTop: 1, borderColor: 'grey.300', bgcolor: offWhite }}>
          {showCoursePicker ? (
            <Stack spacing={0.75} sx={{ mb: hasMoreOptionGroups ? 1 : 0 }}>
              <Typography variant="caption" color="text.secondary">
                Tap courses in priority order, then confirm (
                {pendingCourses.length}
                /4, need at least 2)
              </Typography>
              {pendingCourses.length > 0 ? (
                <Stack direction="row" flexWrap="wrap" gap={0.75}>
                  {pendingCourses.map((course, index) => (
                    <Chip
                      key={`pending-course-${course.id}`}
                      label={`#${index + 1} ${course.label}`}
                      size="small"
                      color="success"
                      onDelete={() => togglePendingCourse(course)}
                    />
                  ))}
                </Stack>
              ) : null}
              <Stack direction="row" flexWrap="wrap" gap={0.75}>
                {options?.courses?.slice(0, 12).map((opt) => {
                  const isSelected = pendingCourseIds.has(opt.id);
                  const priority = pendingCourses.findIndex((course) => course.id === opt.id) + 1;
                  const canSelect = isSelected || pendingCourses.length < 4;

                  return (
                    <Chip
                      key={`course-${opt.id}`}
                      label={isSelected ? `${opt.label} (#${priority})` : opt.label}
                      size="small"
                      clickable={canSelect}
                      color="success"
                      variant={isSelected ? 'filled' : 'outlined'}
                      onClick={() => {
                        if (!canSelect) {
                          return;
                        }

                        togglePendingCourse({ id: opt.id, label: opt.label });
                      }}
                    />
                  );
                })}
              </Stack>
              <Button
                variant="contained"
                color="success"
                size="small"
                disabled={!canConfirmCourses || loading}
                onClick={handleConfirmCourses}
                sx={{ alignSelf: 'flex-start' }}
              >
                Confirm courses
              </Button>
            </Stack>
          ) : null}
          {options?.teeTimePresets?.length ? (
            <Stack
              direction="row"
              flexWrap="wrap"
              gap={0.75}
              sx={{ mb: hasMoreOptionGroups ? 1 : 0 }}
            >
              {options.teeTimePresets.slice(0, 12).map((opt) => (
                <Chip
                  key={`time-${opt.id}`}
                  label={opt.label}
                  size="small"
                  clickable
                  color="success"
                  variant="outlined"
                  onClick={() => handlePick(buildTeeTimePickMessage(opt.label))}
                />
              ))}
            </Stack>
          ) : null}
          {options?.availabilityWindowPresets?.length ? (
            <Stack spacing={0.75} sx={{ mb: options?.availabilityPresets?.length ? 1 : 0 }}>
              <Typography variant="caption" color="text.secondary">
                We cap availability at 3 hours — pick your preferred window:
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={0.75}>
                {options.availabilityWindowPresets.map((opt) => (
                  <Chip
                    key={`avail-window-${opt.id}`}
                    label={opt.label}
                    size="small"
                    clickable
                    color="success"
                    variant="outlined"
                    onClick={() => handlePick(buildAvailabilityWindowPickMessage(opt.label))}
                  />
                ))}
              </Stack>
            </Stack>
          ) : null}
          {options?.availabilityPresets?.length ? (
            <Stack direction="row" flexWrap="wrap" gap={0.75}>
              {options.availabilityPresets.map((minutes) => (
                <Chip
                  key={`avail-${minutes}`}
                  label={formatAvailabilityLabel(minutes)}
                  size="small"
                  clickable
                  color="success"
                  variant="outlined"
                  onClick={() => handlePick(buildAvailabilityPickMessage(minutes))}
                />
              ))}
            </Stack>
          ) : null}
        </Box>
      )}

      <Stack
        direction="row"
        alignItems="flex-end"
        spacing={1}
        sx={{
          px: 2,
          py: 1.5,
          pb: 3,
          borderTop: 1,
          borderColor: 'grey.300',
          bgcolor: offWhite,
          position: 'relative',
        }}
      >
        {showSuggestions ? (
          <ChatMemberMentionList suggestions={suggestions} onSelect={handleSelectMention} />
        ) : null}
        <TextField
          fullWidth
          multiline
          maxRows={4}
          inputRef={inputRef}
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            setCursorIndex(e.target.selectionStart ?? e.target.value.length);
          }}
          onSelect={(e) => {
            const target = e.target as HTMLInputElement | HTMLTextAreaElement;

            setCursorIndex(target.selectionStart ?? input.length);
          }}
          onClick={(e) => {
            const target = e.target as HTMLInputElement | HTMLTextAreaElement;

            setCursorIndex(target.selectionStart ?? input.length);
          }}
          placeholder="Type a message... (@ to mention a member)"
          disabled={loading}
          inputProps={{ maxLength: 4000 }}
          onKeyDown={(e) => {
            if (e.key === 'Tab' && showSuggestions && suggestions.length > 0) {
              e.preventDefault();
              handleSelectMention(suggestions[0]);

              return;
            }

            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              if (canSend) {
                handleSend().catch(() => undefined);
              }
            }
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '22px',
              bgcolor: 'background.paper',
            },
          }}
        />
        <IconButton
          color="success"
          onClick={() => {
            handleSend().catch(() => undefined);
          }}
          disabled={!canSend}
          sx={{
            width: 44,
            height: 44,
            bgcolor: canSend ? 'success.main' : 'action.disabledBackground',
            color: 'success.contrastText',
            '&:hover': { bgcolor: canSend ? 'success.dark' : 'action.disabledBackground' },
          }}
          aria-label="Send message"
        >
          {loading ? <CircularProgress size={22} color="inherit" /> : <SendIcon sx={{ fontSize: 22 }} />}
        </IconButton>
      </Stack>
    </Box>
  );
};

export default ChatAssistant;
